import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import Button from '@atlaskit/button';
import ModalDialog, { ModalTransition, ModalFooter as AKModalFooter, ModalBody, ModalTitle, ModalHeader } from '@atlaskit/modal-dialog';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../../../analytics';
import { BridgeClientError } from '..';
import { navigate } from './navigate';
import { getOrigin } from './getOrigin';
import { useEgressPermissionService } from '../../../utils/useEgressPermissionService';
import { makeUrl } from './makeUrl';
var cssActionItem = css({
  flex: '1 0 auto',
  margin: "0 ".concat("var(--ds-space-050, 4px)")
});
export function useNavigation(_ref) {
  var extension = _ref.extension,
    push = _ref.push;
  var _useState = useState({
      state: 'closed'
    }),
    _useState2 = _slicedToArray(_useState, 2),
    modalState = _useState2[0],
    setModalState = _useState2[1];
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var egressService = useEgressPermissionService('FETCH_CLIENT_SIDE', extension.egress);
  return {
    onNavigate: function () {
      var _onNavigate = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref2) {
        var type, urlString, url, linkType, analyticsAttributes;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              type = _ref2.type, urlString = _ref2.url;
              url = makeUrl(urlString);
              linkType = getLinkType(url, egressService);
              if (!(linkType === 'invalid' || !url)) {
                _context.next = 5;
                break;
              }
              throw new BridgeClientError("link is invalid: ".concat(urlString));
            case 5:
              if (!(linkType === 'trusted')) {
                _context.next = 8;
                break;
              }
              navigate(url.href, type, push);
              return _context.abrupt("return");
            case 8:
              analyticsAttributes = getAnalyticsAttributes(url);
              return _context.abrupt("return", new Promise(function (resolve, reject) {
                // TODO: remove this event once we have switched over to the new event schema
                // https://ecosystem-platform.atlassian.net/browse/EXT-2196
                createAnalyticsEvent({
                  eventType: UI_EVENT_TYPE,
                  data: {
                    action: 'viewed',
                    actionSubject: 'externalLinkModal',
                    attributes: analyticsAttributes
                  }
                }).fire(FORGE_UI_ANALYTICS_CHANNEL);
                setModalState({
                  state: 'open',
                  url: url,
                  onConfirm: function onConfirm() {
                    navigate(url.href, type, push);
                    // TODO: remove this event once we have switched over to the new event schema
                    // https://ecosystem-platform.atlassian.net/browse/EXT-2196
                    createAnalyticsEvent({
                      eventType: UI_EVENT_TYPE,
                      data: {
                        action: 'approved',
                        actionSubject: 'externalLinkModal',
                        attributes: analyticsAttributes
                      }
                    }).fire(FORGE_UI_ANALYTICS_CHANNEL);
                    resolve();
                  },
                  onCancel: function onCancel() {
                    // TODO: remove this event once we have switched over to the new event schema
                    // https://ecosystem-platform.atlassian.net/browse/EXT-2196
                    createAnalyticsEvent({
                      eventType: UI_EVENT_TYPE,
                      data: {
                        action: 'cancelled',
                        actionSubject: 'externalLinkModal',
                        attributes: analyticsAttributes
                      }
                    }).fire(FORGE_UI_ANALYTICS_CHANNEL);
                    reject(new BridgeClientError('cancelled'));
                  }
                });
              }));
            case 10:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      function onNavigate(_x) {
        return _onNavigate.apply(this, arguments);
      }
      return onNavigate;
    }(),
    getModalJsx: function getModalJsx() {
      var _extension$properties;
      if (modalState.state === 'closed') {
        return jsx(ModalTransition, null);
      }
      var ModalFooter = function ModalFooter() {
        return jsx(AKModalFooter, null, jsx("div", {
          // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- to avoid import of css module from @compiled/react
          css: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
          }
        }, jsx(Button, {
          css: {
            padding: 0,
            '> span': {
              margin: 0
            }
          },
          href: "https://go.atlassian.com/forge-help-app-security",
          appearance: "subtle-link",
          target: "_blank",
          rel: "noopener noreferrer"
        }, "Learn more about app security"), jsx("div", {
          // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
          css: css({
            display: 'inline-flex',
            margin: "0 ".concat("var(--ds-space-negative-050, -4px)")
          })
        }, jsx("div", {
          css: cssActionItem
        }, jsx(Button, {
          appearance: "subtle",
          onClick: function onClick() {
            setModalState({
              state: 'closed'
            });
            modalState.onCancel();
          }
        }, "Cancel")), jsx("div", {
          css: cssActionItem
        }, jsx(Button, {
          appearance: "warning",
          onClick: function onClick() {
            modalState.onConfirm();
            setModalState({
              state: 'closed'
            });
          }
        }, "Continue")))));
      };
      return jsx(ModalTransition, null, jsx(ModalDialog, {
        onClose: function onClose() {
          setModalState({
            state: 'closed'
          });
          modalState.onCancel();
        }
      }, jsx(ModalHeader, null, jsx(ModalTitle, {
        appearance: "warning"
      }, "Opening external page on ".concat(modalState.url.hostname))), jsx(ModalBody, null, (_extension$properties = extension.properties) !== null && _extension$properties !== void 0 && _extension$properties.title ? jsx("p", null, jsx("strong", null, extension.properties.title), " is sending you to an external page. Ensure you trust that page before you continue.") : jsx("p", null, jsx("strong", null, "This app"), " is sending you to an external page. Ensure you trust that page before you continue."), jsx("p", {
        css: {
          wordBreak: 'break-all'
        }
      }, modalState.url.href)), jsx(ModalFooter, null)));
    }
  };
}

// Exported for testing only
export function getLinkType(url, egressService) {
  if (!url) {
    return 'invalid';
  }
  if (url.protocol === 'mailto:') {
    return 'trusted';
  }
  try {
    if (egressService.isValidUrl(url.href)) {
      return 'trusted';
    }
    if (url.origin === getOrigin()) {
      // e.g. https://ecosystem.atlassian.net
      return 'trusted';
    }
    if (['https:', 'http:'].includes(url.protocol)) {
      return 'external';
    }
  } catch (_unused) {}
  return 'invalid';
}

// Exported for testing only
export function getAnalyticsAttributes(url) {
  // Don't count the initial `?` in the query string length.
  var queryStringSize = url.search.length > 1 ? url.search.length - 1 : 0;
  return {
    url: url.href,
    queryStringSize: queryStringSize
  };
}